import { Modal } from '../../../components';
import { IFieldWalkthroughDetail, IFieldWalkthroughDetailItem } from '../../../models';
import { Send, Close } from '@mui/icons-material';
import {
  Box,
  ButtonGroup,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack
} from '@mui/material';
import { PunchlistItemAssignedTo } from './assign-to-dropdown-fw';
import { FC, Fragment } from 'react';

interface IAssignToModal {
  isOpen: boolean;
  handleClose: () => void;
  handleContinue: () => void;
  selectedWalkthroughItems: IFieldWalkthroughDetailItem[];
  walkthroughItems: IFieldWalkthroughDetailItem[];
  fieldWalkthrough: IFieldWalkthroughDetail;
  setWalkthroughItems: (items: IFieldWalkthroughDetailItem[]) => void;
  setSelectedWalkthroughItems: (items: IFieldWalkthroughDetailItem[]) => void;
  handleChangedItem: (items: IFieldWalkthroughDetailItem) => void;
}

export const AssignToModal: FC<IAssignToModal> = ({
  isOpen,
  handleClose,
  handleContinue,
  selectedWalkthroughItems,
  walkthroughItems,
  fieldWalkthrough,
  setWalkthroughItems,
  setSelectedWalkthroughItems,
  handleChangedItem
}) => {
  // remove duplicates when possibly clicking an item at the supervisor level and then clicking the same
  // item at the pm response level, so remove any duplicates via fieldWalkthroughItemId
  const items = selectedWalkthroughItems?.filter(
    (value, index, self) =>
      index === self.findIndex(t => t.fieldWalkthroughItemId === value.fieldWalkthroughItemId)
  );

  return (
    <Modal open={isOpen} onClose={handleClose} maxWidth="lg" title="Assign Field Walkthrough Items">
      <Box mt={1}>
        <List>
          {items?.map((item, index) => {
            return (
              <Fragment key={`${index}`}>
                <ListItem key={`${index}`} sx={{ padding: theme => theme.spacing(0.5, 0) }}>
                  <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 0.25, sm: 1 }}
                    justifyItems={{ xs: 'flex-start', sm: 'space-between' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <ListItemText inset={false} primary={item.text} />
                    <Box sx={{ width: { xs: '100%', sm: '50%', md: '35%' }, flex: '0 0 auto' }}>
                      <PunchlistItemAssignedTo
                        sageJobId={fieldWalkthrough?.sageJobId}
                        territoryId={fieldWalkthrough?.territoryId}
                        selectedAssignee={{
                          id: item.assignedTo,
                          accountName: item.assignedTo,
                          displayName: '',
                          personId: null,
                          partnerId: item?.partnerId ?? null,
                          isInternalUser: false
                        }}
                        setSelectedAssignee={val => {
                          // this is for the overall workitems
                          const copy: IFieldWalkthroughDetailItem[] = JSON.parse(
                            JSON.stringify(walkthroughItems)
                          );

                          const selectedIndex = copy.findIndex((c) => c.fieldWalkthroughItemId === item.fieldWalkthroughItemId);
                          const deficientItem = (copy[selectedIndex] = {
                            ...copy[selectedIndex],
                            partnerId: val?.partnerId,
                            assignedTo: val?.accountName
                          });
                          setWalkthroughItems(copy);

                          // this is for the selected workitems
                          const copyNewItems: IFieldWalkthroughDetailItem[] = JSON.parse(
                            JSON.stringify(selectedWalkthroughItems)
                          );
                          const i = copyNewItems.findIndex((c) => c.fieldWalkthroughItemId === item.fieldWalkthroughItemId);
                          copyNewItems[i] = {
                            ...copyNewItems[i],
                            partnerId: val?.partnerId,
                            assignedTo: val?.accountName
                          };

                          handleChangedItem(deficientItem);
                          setSelectedWalkthroughItems(copyNewItems);
                        }}
                      />
                    </Box>
                  </Stack>
                </ListItem>
                <Divider sx={{ margin: theme => theme.spacing(0.5, 0) }} />
              </Fragment>
            );
          })}
        </List>

        <Box mt={1}>
          <ButtonGroup>
            <Button
              type="button"
              onClick={() => {
                handleContinue();
                handleClose();
              }}
              disabled={
                items?.length !== items?.filter(item => item.assignedTo).filter(Boolean).length
              }
              variant="contained"
              color="primary"
              startIcon={<Send />}
            >
              Continue
            </Button>
            <Button
              type="button"
              variant="contained"
              color="inherit"
              startIcon={<Close />}
              onClick={handleClose}
            >
              Cancel
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </Modal>
  );
};
